import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Palette, Type, Compass, Loader2, RefreshCw, Sliders, MessageCircle } from 'lucide-react';
import AIRefinementPanel from '../components/AIRefinementPanel';
import ArtisticDirectionModal from '../components/ArtisticDirectionModal';
import { useTranslation } from 'react-i18next';
import StepReviewModal from '../components/StepReviewModal';

const VisionIdentity = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [showRefinementPanel, setShowRefinementPanel] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showArtisticModal, setShowArtisticModal] = useState(false);
  const [hasSeenResults, setHasSeenResults] = useState(false);

  useEffect(() => {
    if (token) {
      fetchLatestIdentity();
    }
  }, [token]);

  const fetchLatestIdentity = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/vision-identity/latest`, { headers });
      if (response.data.identity) {
        setIdentity(response.data.identity);
      }
    } catch (error) {
      console.error('Error fetching identity:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/vision-identity/generate`,
        { brand_name: brandName || null },
        { headers }
      );
      setIdentity(response.data);
      setHasSeenResults(false); // User hasn't seen results yet
      toast.success(t('toolsPages.common.success'));
      // Review will be shown after user clicks "J'ai compris" button
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toolsPages.common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Handle artistic direction generated
  const handleArtisticDirectionGenerated = (artisticDirection) => {
    // Update identity with new artistic direction
    setIdentity(prev => ({
      ...prev,
      artistic_direction: artisticDirection
    }));
    toast.success('Ta direction artistique est maintenant définie !');
  };

  return (
    <DashboardLayout title={t('toolsPages.visionIdentity.title')}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div className="flex-1">
              <Sparkles className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">{t('toolsPages.visionIdentity.title')}</h2>
              <p className="text-[#A1A1AA] mb-4">
                {t('toolsPages.visionIdentity.subtitle')}
              </p>
              <div className="flex gap-4 items-end">
                <div className="flex-1 max-w-xs">
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">{t('toolsPages.visionIdentity.brandName')}</Label>
                  <Input
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder={t('toolsPages.visionIdentity.brandNamePlaceholder')}
                    className="input-underline"
                    data-testid="brand-name-input"
                  />
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="btn-gold px-8 py-4 flex items-center gap-2"
                  data-testid="generate-identity-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {t('toolsPages.visionIdentity.generating')}
                    </>
                  ) : identity ? (
                    <>
                      <RefreshCw size={18} />
                      {t('toolsPages.visionIdentity.regenerate')}
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      {t('toolsPages.visionIdentity.generate')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Results */}
        {identity ? (
          <div className="space-y-8">
            {/* Vision Identity Overview */}
            {(identity.who_she_is_becoming || identity.positioning_direction || identity.signature_statement) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="text-[#D4AF37]" size={24} />
                  <h3 className="font-serif text-xl text-white">Ta Vision d'Identité</h3>
                </div>
                
                <div className="space-y-6">
                  {identity.who_she_is_becoming && (
                    <div>
                      <span className="text-[#D4AF37] text-xs uppercase tracking-wider block mb-2">Qui tu deviens</span>
                      <p className="text-white leading-relaxed">{identity.who_she_is_becoming}</p>
                    </div>
                  )}
                  
                  {identity.positioning_direction && (
                    <div className="pt-4 border-t border-[#27272A]">
                      <span className="text-[#D4AF37] text-xs uppercase tracking-wider block mb-2">Direction de positionnement</span>
                      <p className="text-[#A1A1AA] leading-relaxed">{identity.positioning_direction}</p>
                    </div>
                  )}
                  
                  {identity.signature_statement && (
                    <div className="pt-4 border-t border-[#27272A]">
                      <span className="text-[#D4AF37] text-xs uppercase tracking-wider block mb-2">Ta signature</span>
                      <p className="text-white leading-relaxed italic font-serif text-lg">"{identity.signature_statement}"</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Interactive AI Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-2"
            >
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={loading}
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
                data-testid="regenerate-identity-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                {t('visionIdentityPage.regenerate')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRefinementPanel(true)}
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
                data-testid="adjust-identity-btn"
              >
                <Sliders size={16} className="mr-2" />
                {t('visionIdentityPage.adjust')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRefinementPanel(true)}
                className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 py-3"
                data-testid="ask-ai-identity-btn"
              >
                <MessageCircle size={16} className="mr-2" />
                {t('visionIdentityPage.askAi')}
              </Button>
            </motion.div>

            {/* Brand Colors / Color Energy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-premium p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Palette className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">{t('visionIdentityPage.brandColors')}</h3>
              </div>
              
              {identity.brand_colors ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  {identity.brand_colors?.map((color, i) => (
                    <div key={i} className="text-center">
                      <div 
                        className="aspect-square border border-[#27272A] mb-3" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-white text-sm font-mono">{color}</span>
                      <p className="text-[#52525B] text-xs mt-1">
                        {identity.color_meanings?.[i]}
                      </p>
                    </div>
                  ))}
                </div>
              ) : identity.visual_direction?.color_energy ? (
                <p className="text-[#A1A1AA] leading-relaxed">
                  {identity.visual_direction.color_energy}
                </p>
              ) : (
                <p className="text-[#52525B] italic">Générez votre identité pour voir vos couleurs de marque.</p>
              )}
            </motion.div>

            {/* Visual Aesthetic & Tone */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Compass className="text-[#D4AF37]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('visionIdentityPage.visualAesthetic')}</h3>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {identity.visual_aesthetic || identity.visual_direction?.primary_aesthetic || "Non défini"}
                </p>
                {identity.visual_direction?.imagery_style && (
                  <p className="text-[#71717A] leading-relaxed mt-4 pt-4 border-t border-[#27272A]">
                    <span className="text-[#D4AF37] text-xs uppercase tracking-wider block mb-2">Style d'imagerie</span>
                    {identity.visual_direction.imagery_style}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Type className="text-[#E5C585]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('visionIdentityPage.brandTone')}</h3>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {identity.brand_tone || identity.brand_energy || "Non défini"}
                </p>
              </motion.div>
            </div>

            {/* Typography */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-premium p-8"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif text-xl text-white">{t('visionIdentityPage.typography')}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRefinementPanel(true)}
                  className="text-[#D4AF37] hover:text-[#E5C585] hover:bg-[#D4AF37]/10"
                  data-testid="edit-typography-btn"
                >
                  <Sliders size={16} className="mr-2" />
                  {t('visionIdentityPage.customize')}
                </Button>
              </div>
              <p className="text-[#52525B] text-sm mb-6">{t('visionIdentityPage.typographyGuide')}</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div 
                  className="border border-[#27272A] p-6 hover:border-[#D4AF37]/50 transition-colors cursor-pointer group"
                  onClick={() => setShowRefinementPanel(true)}
                >
                  <span className="text-[#E5C585] text-xs font-accent tracking-widest">{t('visionIdentityPage.headingLabel')}</span>
                  <p className="text-white text-2xl font-serif mt-2">
                    {identity.typography_suggestions?.heading || 'Playfair Display'}
                  </p>
                  <p className="text-[#52525B] text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('visionIdentityPage.clickToCustomize')}
                  </p>
                </div>
                <div 
                  className="border border-[#27272A] p-6 hover:border-[#D4AF37]/50 transition-colors cursor-pointer group"
                  onClick={() => setShowRefinementPanel(true)}
                >
                  <span className="text-[#E5C585] text-xs font-accent tracking-widest">{t('visionIdentityPage.bodyLabel')}</span>
                  <p className="text-white text-lg mt-2">
                    {identity.typography_suggestions?.body || 'Lora'}
                  </p>
                  <p className="text-[#52525B] text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('visionIdentityPage.clickToCustomize')}
                  </p>
                </div>
                <div 
                  className="border border-[#27272A] p-6 hover:border-[#D4AF37]/50 transition-colors cursor-pointer group"
                  onClick={() => setShowRefinementPanel(true)}
                >
                  <span className="text-[#E5C585] text-xs font-accent tracking-widest">{t('visionIdentityPage.accentLabel')}</span>
                  <p className="text-white text-lg font-accent mt-2">
                    {identity.typography_suggestions?.accent || 'Didot'}
                  </p>
                  <p className="text-[#52525B] text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('visionIdentityPage.clickToCustomize')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Artistic Direction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="card-premium p-8"
              data-testid="artistic-direction-card"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl text-white">{t('visionIdentityPage.artisticDirection')}</h3>
                {identity.artistic_direction && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRefinementPanel(true)}
                    className="text-[#D4AF37] hover:text-[#E5C585] hover:bg-[#D4AF37]/10"
                    data-testid="edit-artistic-btn"
                  >
                    <Sliders size={16} className="mr-2" />
                    {t('visionIdentityPage.customize')}
                  </Button>
                )}
              </div>
              
              {identity.artistic_direction ? (
                <p className="text-[#A1A1AA] leading-relaxed text-lg">
                  {identity.artistic_direction}
                </p>
              ) : (
                /* Guided Experience for Empty State */
                <div className="space-y-6">
                  {/* Introduction */}
                  <div className="border-l-2 border-[#D4AF37] pl-4 py-2">
                    <p className="text-[#E5C585] text-sm leading-relaxed">
                      {t('visionIdentityPage.artisticIntro')}
                    </p>
                  </div>
                  
                  {/* Guided Questions */}
                  <div className="bg-[#0A0A0A]/50 p-6 border border-[#1C1C1C]">
                    <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-4">
                      {t('visionIdentityPage.artisticQuestionsTitle')}
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        <span>{t('visionIdentityPage.artisticQ1')}</span>
                      </li>
                      <li className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        <span>{t('visionIdentityPage.artisticQ2')}</span>
                      </li>
                      <li className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        <span>{t('visionIdentityPage.artisticQ3')}</span>
                      </li>
                      <li className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        <span>{t('visionIdentityPage.artisticQ4')}</span>
                      </li>
                      <li className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        <span>{t('visionIdentityPage.artisticQ5')}</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Reassurance Message */}
                  <p className="text-[#52525B] text-sm italic text-center">
                    {t('visionIdentityPage.artisticReassurance')}
                  </p>
                  
                  {/* CTA Button */}
                  <Button
                    onClick={() => setShowArtisticModal(true)}
                    className="w-full btn-gold py-4 flex items-center justify-center gap-3"
                    data-testid="define-artistic-direction-btn"
                  >
                    <Sparkles size={20} />
                    <span className="font-medium">{t('visionIdentityPage.defineArtisticDirection')}</span>
                  </Button>

                  {/* Call to Action - Triggers Review */}
                  {!hasSeenResults && (
                    <div className="card-gold p-5 text-center mt-6">
                      <p className="text-white mb-3 text-sm">As-tu pris le temps de découvrir ton identité ?</p>
                      <Button
                        onClick={() => {
                          setHasSeenResults(true);
                          setShowReviewModal(true);
                        }}
                        className="btn-gold px-6 py-2"
                        data-testid="understood-btn"
                      >
                        J'ai compris, donner mon avis ✨
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-16 text-center"
          >
            <Sparkles className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">{t('visionIdentityPage.noIdentityYet')}</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto mb-8">
              {t('visionIdentityPage.noIdentityDesc')}
            </p>
          </motion.div>
        )}
      </div>
      
      {/* AI Refinement Panel */}
      <AIRefinementPanel
        isOpen={showRefinementPanel}
        onClose={() => setShowRefinementPanel(false)}
        toolType="vision_identity"
        currentResult={identity}
        onResultUpdate={(newResult) => {
          setIdentity({ ...identity, ...newResult });
          toast.success(t('visionIdentityPage.identityUpdated'));
        }}
        suggestedPrompts={[
          t('visionIdentityPage.prompts.luxurious'),
          t('visionIdentityPage.prompts.feminine'),
          t('visionIdentityPage.prompts.bolder'),
          t('visionIdentityPage.prompts.explainColors')
        ]}
      />

      {/* Step Review Modal */}
      <StepReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        stepId="vision_identity"
        stepName="Créer ton identité"
        onValidated={() => setShowReviewModal(false)}
      />

      {/* Artistic Direction Modal */}
      <ArtisticDirectionModal
        isOpen={showArtisticModal}
        onClose={() => setShowArtisticModal(false)}
        onGenerated={handleArtisticDirectionGenerated}
        existingIdentity={identity}
      />
    </DashboardLayout>
  );
};

export default VisionIdentity;
